import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  createEntityAdapter,
  EntityState,
} from '@reduxjs/toolkit';
import { AppState } from '.';
import { AnyPromotionInfo, SerializedException, exceptionOf } from '../models';
import * as PromotionService from '../services/promotion';

export const PROMOTION_FEATURE_KEY = 'promotion';

const promotionAdapter = createEntityAdapter<AnyPromotionInfo>();

interface PromotionState extends EntityState<AnyPromotionInfo> {
  activePromotion: AnyPromotionInfo | null;
  loading: boolean;
  errors: SerializedException[];
}

const createInitialState = (): PromotionState => promotionAdapter.getInitialState({
  activePromotion: null,
  loading: false,
  errors: [],
});

// Async thunks
export const doFetchActivePromotion = createAsyncThunk(
  'promotion/fetchActivePromotion',
  async (_, { rejectWithValue }) => {
    try {
      const promotion = await PromotionService.getActivePromotion();
      return promotion;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const promotionSlice = createSlice({
  name: PROMOTION_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder
      // Fetch active promotion
      .addCase(doFetchActivePromotion.pending, state => {
        state.loading = true;
        state.errors = [];
      })
      .addCase(
        doFetchActivePromotion.fulfilled,
        (state, action: PayloadAction<AnyPromotionInfo | null>) => {
          state.loading = false;
          state.activePromotion = action.payload;
        },
      )
      .addCase(doFetchActivePromotion.rejected, (state, action) => {
        state.loading = false;
        state.errors = [action.payload as SerializedException];
      });
  },
});

// Selectors
const selectPromotionState = (state: AppState) => state[PROMOTION_FEATURE_KEY];

export const selectActivePromotion = (state: AppState) => (
  selectPromotionState(state).activePromotion
);

// Reducer
export default promotionSlice.reducer;
